import React, { useState, useEffect, useCallback } from 'react';
import styled from 'styled-components';
import NotificationButton from './NotificationButton';
import { isSubscribedToNotifications } from '../services/firebaseService';

// Types for PWA install prompt
interface BeforeInstallPromptEvent extends Event {
  readonly platforms: string[];
  readonly userChoice: Promise<{
    outcome: 'accepted' | 'dismissed';
    platform: string;
  }>;
  prompt(): Promise<void>;
}

// Enhanced app state types
type AppState = 'not-installable' | 'installable' | 'installing' | 'installed';

// Styled components with cleaner border design
const InstallButtonContainer = styled.button<{ visible: boolean; appState: AppState }>`
  position: fixed;
  right: 16px;
  top: calc(50% + 150px);
  background: none;
  border: none;
  width: 48px;
  height: 48px;
  display: ${({ visible }) => visible ? 'flex' : 'none'};
  justify-content: center;
  align-items: center;
  color: white;
  cursor: pointer;
  z-index: 3;
  padding: 6px;
  
  &:hover {
    opacity: 0.8;
  }
  
  &:active {
    transform: scale(0.95);
  }
`;

const ToastNotification = styled.div<{ visible: boolean; type: 'success' | 'info' | 'error' | 'warning' }>`
  position: fixed;
  top: 20px;
  left: 50%;
  transform: translateX(-50%);
  background: ${({ type }) => 
    type === 'success' ? 'rgba(46, 204, 113, 0.95)' :
    type === 'error' ? 'rgba(231, 76, 60, 0.95)' :
    type === 'warning' ? 'rgba(243, 156, 18, 0.95)' :
    'rgba(52, 152, 219, 0.95)'
  };
  color: white;
  padding: 12px 20px;
  border-radius: 25px;
  backdrop-filter: blur(10px);
  border: 1px solid rgba(255, 255, 255, 0.2);
  z-index: 1000;
  font-size: 14px;
  font-weight: 500;
  opacity: ${({ visible }) => visible ? 1 : 0};
  transform: ${({ visible }) => 
    visible ? 'translateX(-50%) translateY(0)' : 'translateX(-50%) translateY(-20px)'
  };
  transition: all 0.3s ease;
  pointer-events: none;
  max-width: 90vw;
  text-align: center;
  box-shadow: 0 8px 20px rgba(0, 0, 0, 0.3);
`;

// Platform detection utilities
const isIOS = (): boolean => {
  if (typeof window === 'undefined') return false;
  return /iPad|iPhone|iPod/.test(navigator.userAgent) || 
         (navigator.platform === 'MacIntel' && navigator.maxTouchPoints > 1);
};

const isAndroid = (): boolean => {
  if (typeof window === 'undefined') return false;
  return /Android/.test(navigator.userAgent);
};

const isMobile = (): boolean => {
  if (typeof window === 'undefined') return false;
  return /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent) || 
         window.innerWidth <= 768;
};

// Enhanced standalone detection with multiple methods
const isStandalone = (): boolean => {
  if (typeof window === 'undefined') return false;
  
  // Method 1: Check for standalone mode (iOS Safari)
  const standaloneMode = (window.navigator as any).standalone === true;
  
  // Method 2: Check display-mode media query (Chrome/Edge)
  const displayModeStandalone = window.matchMedia('(display-mode: standalone)').matches;
  
  // Method 3: Check for minimal-ui or fullscreen (some PWAs)
  const displayModeMinimalUI = window.matchMedia('(display-mode: minimal-ui)').matches;
  const displayModeFullscreen = window.matchMedia('(display-mode: fullscreen)').matches;
  
  return standaloneMode || displayModeStandalone || displayModeMinimalUI || displayModeFullscreen;
};

// Enhanced app install status detection
const getAppInstallStatus = (hasDeferredPrompt: boolean): AppState => {
  if (typeof window === 'undefined') return 'not-installable';
  
  // Check if running in standalone mode (installed and in app)
  if (isStandalone()) {
    return 'installed';
  }
  
  // Check if can be installed (has beforeinstallprompt)
  if (hasDeferredPrompt) {
    return 'installable';
  }
  
  // Cannot be installed
  return 'not-installable';
};

// PWA Install Button Component
const PWAInstallButton: React.FC = () => {
  const [deferredPrompt, setDeferredPrompt] = useState<BeforeInstallPromptEvent | null>(null);
  const [appState, setAppState] = useState<AppState>('not-installable');
  const [showInstallButton, setShowInstallButton] = useState(false);
  const [showNotificationButton, setShowNotificationButton] = useState(false);
  const [isInstalling, setIsInstalling] = useState(false);
  const [toast, setToast] = useState<{
    visible: boolean;
    message: string;
    type: 'success' | 'info' | 'error' | 'warning';
  }>({
    visible: false,
    message: '',
    type: 'info'
  });

  // Show toast notification
  const showToast = useCallback((message: string, type: 'success' | 'info' | 'error' | 'warning' = 'info') => {
    setToast({ visible: true, message, type });
    setTimeout(() => {
      setToast(prev => ({ ...prev, visible: false }));
    }, 4000);
  }, []);

  // ✅ IMPROVED: Update app install status with improved logic
  const updateAppStatus = useCallback(() => {
    const status = getAppInstallStatus(!!deferredPrompt);
    const inStandalone = isStandalone();
    const onMobile = isMobile();
    
    setAppState(status);
    
    console.log('🔍 PWA Status Check:', { 
      status, 
      onMobile,
      inStandalone,
      hasDeferredPrompt: !!deferredPrompt,
      userAgent: navigator.userAgent.substring(0, 50) 
    });
    
    // ✅ FIXED LOGIC: Clearer button visibility control
    if (!onMobile) {
      // Desktop - hide PWA install (mobile-first feature)
      setShowInstallButton(false);
      setShowNotificationButton(false);
      console.log('✅ Desktop mode - hiding PWA buttons (mobile-first design)');
    } else {
      // Mobile device logic
      const shouldShowInstall = !inStandalone && status === 'installable';
      const shouldShowNotification = (
        // Show in browser when PWA is installed but not in standalone
        (!inStandalone && status === 'installed') ||
        // OR show in PWA mode when user needs to subscribe (FIXED: was hidden before)
        (inStandalone && !isSubscribedToNotifications())
      );
      
      setShowInstallButton(shouldShowInstall);
      setShowNotificationButton(shouldShowNotification);
      
      console.log('✅ Mobile logic - Install:', shouldShowInstall, 'Notification:', shouldShowNotification);
      
      if (inStandalone) {
        console.log('✅ PWA standalone mode - showing notification button for subscription');
      } else if (status === 'installable') {
        console.log('✅ PWA installable - showing install button');
      } else if (status === 'installed') {
        console.log('✅ PWA installed, in browser - showing notification access');
      } else {
        console.log('✅ PWA not available - hiding all buttons');
      }
    }
    
    return status;
  }, [deferredPrompt]);

  // Handle beforeinstallprompt event (Android Chrome/Edge)
  useEffect(() => {
    const handleBeforeInstallPrompt = (e: Event) => {
      console.log('🔵 PWA: beforeinstallprompt event fired - App is installable!');
      e.preventDefault();
      const promptEvent = e as BeforeInstallPromptEvent;
      setDeferredPrompt(promptEvent);
      
      // Update status immediately
      setTimeout(() => updateAppStatus(), 100);
    };

    const handleAppInstalled = () => {
      console.log('🎉 PWA: App installed successfully via native prompt');
      setAppState('installed');
      setIsInstalling(false);
      setDeferredPrompt(null);
      
      // Hide install button and show success
      setShowInstallButton(false);
      showToast('✅ App đã được cài đặt thành công!', 'success');
      
      // Show notification button after install success (only if not in standalone)
      setTimeout(() => {
        if (!isStandalone()) {
          setShowNotificationButton(true);
          console.log('🔔 Notification button shown after app installation');
        }
      }, 2000);
    };

    const handleVisibilityChange = () => {
      // Check if app was installed when user returns to tab
      if (!document.hidden) {
        setTimeout(() => {
          updateAppStatus();
        }, 500);
      }
    };

    window.addEventListener('beforeinstallprompt', handleBeforeInstallPrompt);
    window.addEventListener('appinstalled', handleAppInstalled);
    document.addEventListener('visibilitychange', handleVisibilityChange);

    return () => {
      window.removeEventListener('beforeinstallprompt', handleBeforeInstallPrompt);
      window.removeEventListener('appinstalled', handleAppInstalled);
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    };
  }, [updateAppStatus, showToast]);

  // ✅ IMPROVED: Better initialization
  useEffect(() => {
    console.log('🚀 PWA: Component mounted, checking install status...');
    
    // Initial status check
    updateAppStatus();
    
    // Check periodically for install status changes  
    const interval = setInterval(() => {
      updateAppStatus();
    }, 5000); // Check every 5 seconds
    
    return () => {
      clearInterval(interval);
    };
  }, [updateAppStatus]);

  // Handle install/open button click
  const handleInstallButtonClick = useCallback(async () => {
    console.log('🔵 PWA: Install button clicked, current state:', appState);
    
    if (isInstalling) {
      return; // Prevent multiple clicks during installation
    }
    
    try {
      if (appState === 'installable' && deferredPrompt) {
        // App can be installed - use native prompt
        setIsInstalling(true);
        setAppState('installing');
        
        console.log('🔵 PWA: Showing native install prompt');
        showToast('💫 Đang chuẩn bị cài đặt...', 'info');
        
        await deferredPrompt.prompt();
        
        const choiceResult = await deferredPrompt.userChoice;
        console.log('🔵 PWA: User choice:', choiceResult.outcome);
        
        if (choiceResult.outcome === 'accepted') {
          showToast('🚀 Đang cài đặt app...', 'info');
          // Don't reset state here - wait for 'appinstalled' event
        } else {
          showToast('❌ Cài đặt bị hủy', 'warning');
          setIsInstalling(false);
          setAppState('installable'); // Reset state
        }
        
        setDeferredPrompt(null);
      } else {
        // Should not happen if logic is correct
        console.warn('🔵 PWA: Button clicked but app is not installable');
        showToast('⚠️ App không thể cài đặt', 'warning');
      }
      
    } catch (error) {
      console.error('❌ PWA: Install failed:', error);
      showToast('❌ Không thể cài đặt app', 'error');
      setIsInstalling(false);
      setAppState(appState === 'installing' ? 'installable' : appState); // Reset state
    }
  }, [appState, deferredPrompt, isInstalling, showToast]);

  // Handle notification subscription changes
  const handleNotificationSubscriptionChange = useCallback((subscribed: boolean) => {
    if (subscribed) {
      console.log('🔔 PWA: User subscribed to notifications');
      showToast('🔔 Đã bật thông báo!', 'success');
      // Hide notification button after successful subscription
      setTimeout(() => {
        setShowNotificationButton(false);
        console.log('🔔 PWA: Hiding notification button after subscription');
      }, 3000);
    } else {
      console.log('🔔 PWA: User unsubscribed from notifications');
      // Keep notification button visible for re-subscription
    }
  }, [showToast]);

  // ✅ DEBUG: Add comprehensive logging
  useEffect(() => {
    const logStatus = () => {
      console.log('🔍 PWA Button Status:', {
        appState,
        showInstallButton,
        showNotificationButton,
        isStandalone: isStandalone(),
        isMobile: isMobile(),
        hasDeferredPrompt: !!deferredPrompt,
        userAgent: navigator.userAgent.substring(0, 100)
      });
    };

    logStatus();
  }, [appState, showInstallButton, showNotificationButton, deferredPrompt]);

  const buttonTitle = isInstalling ? 'Đang cài đặt...' : 'Cài đặt app';
  const buttonAriaLabel = isInstalling ? 'Đang cài đặt ứng dụng' : 'Cài đặt ứng dụng';

  return (
    <>
      {/* PWA Install Button - Only show on mobile */}
      <InstallButtonContainer
        visible={showInstallButton && isMobile()}
        appState={isInstalling ? 'installing' : appState}
        onClick={handleInstallButtonClick}
        aria-label={buttonAriaLabel}
        title={buttonTitle}
        disabled={isInstalling}
      >
        {isInstalling ? (
          // Loading spinner
          <svg width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
            <circle cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="2" strokeOpacity="0.3"/>
            <path d="M12 2 A10 10 0 0 1 22 12" stroke="currentColor" strokeWidth="2" strokeLinecap="round">
              <animateTransform
                attributeName="transform"
                attributeType="XML"
                type="rotate"
                from="0 12 12"
                to="360 12 12"
                dur="1s"
                repeatCount="indefinite"
              />
            </path>
          </svg>
        ) : (
          // Install app icon - Download style
          <svg width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
            <rect x="5" y="2" width="14" height="20" rx="2" ry="2" stroke="currentColor" strokeWidth="2" fill="none"/>
            <rect x="7" y="4" width="10" height="12" rx="1" fill="currentColor" fillOpacity="0.2"/>
            <path d="M12 8v6m-3-3l3 3 3-3" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
            <circle cx="12" cy="18" r="1" fill="currentColor"/>
          </svg>
        )}
      </InstallButtonContainer>

      {/* Toast Notification */}
      <ToastNotification
        visible={toast.visible}
        type={toast.type}
      >
        {toast.message}
      </ToastNotification>

      {/* Notification Button - Show when PWA is installed but user is in browser */}
      <NotificationButton 
        visible={showNotificationButton}
        onSubscriptionChange={handleNotificationSubscriptionChange}
      />
    </>
  );
};

export default PWAInstallButton; 